<script>
	import { v4 as uuidv4 } from "uuid";
	import { modalState } from "@app/store/modalStore.js";
	import { externalDataState } from "@app/store/externalDataStore.js";
	import { uiState } from "@app/store/uiStore.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { fade } from "svelte/transition";
	import { projectState } from "@app/store/projectStore.js";
	import { playerState } from "@app/store/playerStore.js";
	import { speakerState } from "@app/store/speakerStore.js";
	// import { tagsState } from "@app/store/tagsStore.js";
	import { metadataState } from "@app/store/metadataStore.js";
	import { eventGroupState } from "@app/store/eventGroupStore.js";
	import { onMount } from "svelte";

	import _Event from "@app/external/cc-lib/dist/classes/event.js";
	import _EventGroup from "@app/external/cc-lib/dist/classes/eventGroup.js";
	import _Project from "@app/external/cc-lib/dist/classes/project.js";
	import loadMedia from "@app/external/cc-lib/dist/functions/projects/loadMedia.js";
    import stripTags from "@app/external/cc-lib/dist/functions/quill/stripTags.js";
	import decode from "@app/external/cc-lib/dist/functions/decode.js";
	import automaticOs from "@app/external/cc-lib/dist/functions/special/automaticOffset.js";
	import defaults from "@app/external/cc-lib/dist/lib/defaults.js";
	import examplePayload from "@app/examplePayload.js";

	/* HLS */
	// import Hls from 'hls.js'

	console.info("DEBUG: Initializing project loader.");

	let progress = 0,
		statusMsg = "Starting import...";

	const uid = uuidv4();

	async function updateStatus(statusInfo) {
		progress = statusInfo.progress;
		statusMsg = statusInfo.msg;
	}

	onMount(async () => {
		console.info("DEBUG: Project loader mounted.");
		try {
			let projectInfo, captionTrackId, apiPath, returnTo;
			let playerWidth = document.getElementById("PlayerWrapper").clientWidth;
			let playerHeight = document.getElementById("PlayerWrapper").clientHeight;
			let testingVariables = JSON.parse(localStorage.getItem("trms-testing")) || {};
			console.info("DEBUG: Testing variables loaded from local storage: " + JSON.stringify(testingVariables));

			$uiState.timeline = false;
			$projectState.selected = false;
			$eventGroupState = [];
			$speakerState = [];
			// $tagsState = [];
			$metadataState = [];
			playerState.updateDuration(false);

			await updateStatus({
				progress: 5,
				msg: "Loading project info...",
			});

			if (testingVariables.captionTrackId) {
				console.info("DEBUG: Caption track id set in local storage: " + testingVariables.captionTrackId);
				captionTrackId = testingVariables.captionTrackId;
			} else {
				console.info("DEBUG: Parsing URL params from URL: " + window.location.href);
				let urlString = window.location.href;
				let url = new URL(urlString);
				captionTrackId = url.searchParams.get("texttrack");
				returnTo = url.searchParams.get("return-to");
			}

			console.info("DEBUG: Caption track id is set to " + captionTrackId);
			console.info("DEBUG: Return To Param (return-to) set to " + returnTo);
			if (captionTrackId) {
				try {
					if (testingVariables.apiPath) {
						console.info(`DEBUG: Getting project information from: ${testingVariables.apiPath.replace("textrackid", captionTrackId)}`);
						apiPath = testingVariables.apiPath.replace("textrackid", captionTrackId);
					} else {
						console.info(`DEBUG: Getting project information from: /cablecastapi/v1/texttracks/edit/${captionTrackId}/load`);
						apiPath = `/cablecastapi/v1/texttracks/edit/${captionTrackId}/load`;
					}

					let getProjectInfoRes = await fetch(apiPath);
					console.info(`DEBUG: Get Project info Res: ${getProjectInfoRes}`);
					projectInfo = await getProjectInfoRes.json();
					console.info("Project Info:", projectInfo);
				} catch (err) {
					console.log(err);
					console.error(`ERROR: ${err.message}`);
					console.info("Loading example payload instead: ", examplePayload);
					projectInfo = examplePayload;
				}
			} else {
				console.error("ERROR: Caption track id is null. Loading test payload instead.");
				console.info("Example Payload: ", examplePayload);
				projectInfo = examplePayload;
			}

			await updateStatus({
				progress: 40,
				msg: "Creating project...",
			});

			projectInfo.projectName = projectInfo.projectName || "Unknown";
			projectInfo.frameRate = projectInfo.frameRate || 29.97;

			console.info(`DEBUG: Creating new project.`);
			projectInfo.returnTo = returnTo;
			$externalDataState = JSON.parse(JSON.stringify(projectInfo));
			console.info("EXTERNAL DATA STORE: ", JSON.stringify($externalDataState, null, 4));

			$projectState = new _Project({
				name: projectInfo.projectName,
				frameRate: parseFloat(projectInfo.frameRate),
				dropFrame: projectInfo.dropFrame,
			});

			await updateStatus({
				progress: 60,
				msg: "Loading caption tracks",
			});

			console.info(`DEBUG: Importing caption tracks. ${JSON.stringify(projectInfo.captionTracks)}`);
			projectInfo.captionTracks.forEach(async (track, index, tracks) => {
				console.info(`DEBUG: Importing track`, JSON.stringify(track));
				let eventGroupOptions = {
					type: "subtitle",
					name: `Subtitles ${index + 1}`,
					language: track.language || "en-US",
					rtl: false,
					maxCps: 9999,
					maxWpm: 9999,
					maxChars: track.maxChars || 9999,
					maxLines: track.maxLines || 9999,
					overlap: track.overlaps,
				};

				$eventGroupState = [...$eventGroupState, new _EventGroup(eventGroupOptions)];
				let options = new defaults.options({
					profile: track.type || "scenerist",
					formatOptions: [],
					frameRate: projectInfo.frameRate,
					dropFrame: projectInfo.dropFrame,
					window: {
						width: playerWidth,
						height: playerHeight,
						xOffset: playerWidth * 0.1,
						yOffset: playerHeight * 0.1,
					},
				});

				console.info(`DEBUG: Importing track contents from ${track.url}`);
				let fileContents = await (await fetch(track.url)).text();
				let decodedEvGroup = await decode(fileContents, options);

				decodedEvGroup = automaticOs(decodedEvGroup, projectInfo.frameRate);
				/* Remove Blank Events */
				decodedEvGroup.events = decodedEvGroup.events.filter((event) => {
					return stripTags(event.text) !== "";
				});

                console.log('DEBUG: Decoded Event Group:', decodedEvGroup);

                if (decodedEvGroup.speakers.length > 0){
                    console.log(decodedEvGroup.speakers);
                    $speakerState = [...$speakerState, ...decodedEvGroup.speakers];
                }

                //reset yOffset and xOffset to 0
                decodedEvGroup.events.forEach((event, i, events) => {
                    events[i].yOffset = 0;
                    events[i].xOffset = 0;
                });

				$eventGroupState[$eventGroupState.length - 1].events = [...decodedEvGroup.events];
			});

			console.info(`DEBUG: Importing caption track(s) complete.`);

			$projectState.selected = 0;
			await updateStatus({
				progress: 75,
				msg: "Loading media...",
			});

			console.info(`DEBUG: Loading media.`);

			if (projectInfo.mediaUrl) {
				$projectState = loadMedia($projectState, {
					projectName: projectInfo.projectName,
					storage: "Cloud Storage",
					mediaUrl: projectInfo.mediaUrl,
					files: false,
					frameRate: parseFloat(projectInfo.frameRate),
					dropFrame: projectInfo.dropFrame,
					aspectRatio: projectInfo.aspectRatio || "16:9",
				});

				console.info(`DEBUG: Importing media from ${$projectState.media.path}.`);
				/* Load Media Into Player */
				let mediaType = "video/mp4";
				
				// Handle different media formats like in MediaImport
				if ($projectState.media.type === "video/quicktime") {
					mediaType = "video/mp4";
				} else if ($projectState.media.type === "video/x-matroska") {
					mediaType = "video/mkv";
				} else if ($projectState.media.type) {
					mediaType = $projectState.media.type;
				}
				
				// Try to get file size for large files
				try {
					const response = await fetch($projectState.media.path, {
						method: "HEAD"
					});
					const fileSize = response.headers.get("content-length");
					if (fileSize) {
						const sizeInBytes = parseInt(fileSize);
						const sizeInMegabytes = sizeInBytes / (1024 * 1024);
						if (sizeInMegabytes < 512) {
							$projectState.media.useFallback = false;
						}
					}
				} catch (err) {
					console.info("Could not determine file size:", err);
				}

				// Set player source with proper media type
                player.src = {src: $projectState.media.path, type: mediaType};
			} else if (projectInfo.thumbnailUrl) {
				let source = {
					src: "",
					type: "video/mp4",
				};

				player.source = {
					type: "video",
					poster: projectInfo.thumbnailUrl,
					sources: [source],
				};
			}

			await updateStatus({
				progress: 100,
				msg: "Done",
			});
			console.info(`DEBUG: DONE.`);
			console.info(`DEBUG: Closing project loader.`);
		
			modalState.hideModal();
		} catch (err) {
			console.info(err, err.message);
			toast.push(`External Project Import Failed. ${err.message}`, {classes: ['toast-danger']});
			modalState.hideModal();
		} finally {
			setTimeout(() => {
				$uiState.timeline = true;
			}, 1000);
		}
	});
</script>

<div transition:fade={{ duration: 100 }} class="modal {$modalState === 'loadExternalProjectCustom' ? 'show d-block' : ''}" role="dialog" tabindex="-1" id="LoadExternalProjectModal">
	<div class="modal-dialog modal-dialog-centered" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title">Project Import</h4>
				<button type="button" class="btn-close" aria-label="Close" on:click={modalState.hideModal} />
			</div>
			<div class="modal-body">
				<p><i class="bi bi-exclamation-diamond-fill" /> {statusMsg}</p>
				<div class="progress">
					<div class="progress-bar bg-success progress-bar-striped progress-bar-animated" role="progressbar" style="width: {progress}%;" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">{progress}%</div>
				</div>
			</div>
		</div>
	</div>
</div>
